import { create } from "zustand";
import { getPharmacy } from "@/lib/api/pharmacy";
import { getUsers } from "@/lib/api/users";

interface PharmacyDetails {
  ncpdp_id?: string | null;
  dea_registration_number?: string | null;
  dea_expiration_date?: string | null;
  state_license_number?: string | null;
  license_expiration_date?: string | null;
  [key: string]: unknown;
}

interface StaffUser {
  id: string;
  initials: string;
  full_name: string | null;
  role: string;
}

interface PharmacyStoreState {
  pharmacy: PharmacyDetails | null;
  users: StaffUser[];
  isPharmacyLoaded: boolean;
  isUsersLoaded: boolean;
  fetchPharmacy: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  invalidatePharmacy: () => void;
  invalidateUsers: () => void;
  reset: () => void;
}

let pharmacyPromise: Promise<void> | null = null;
let usersPromise: Promise<void> | null = null;

export const usePharmacyStore = create<PharmacyStoreState>((set, get) => ({
  pharmacy: null,
  users: [],
  isPharmacyLoaded: false,
  isUsersLoaded: false,

  fetchPharmacy: async () => {
    if (get().isPharmacyLoaded || pharmacyPromise) return;
    pharmacyPromise = (async () => {
      try {
        const data = await getPharmacy();
        const pharmacy = (data as { data?: PharmacyDetails }).data ?? data;
        set({ pharmacy: pharmacy as PharmacyDetails, isPharmacyLoaded: true });
      } catch {
        set({ isPharmacyLoaded: true });
      } finally {
        pharmacyPromise = null;
      }
    })();
    return pharmacyPromise;
  },

  fetchUsers: async () => {
    if (get().isUsersLoaded || usersPromise) return;
    usersPromise = (async () => {
      try {
        const data = await getUsers();
        const usersList =
          (data as { data?: { users: StaffUser[] } }).data?.users ||
          (data as { users?: StaffUser[] }).users ||
          [];
        set({ users: usersList, isUsersLoaded: true });
      } catch {
        set({ isUsersLoaded: true });
      } finally {
        usersPromise = null;
      }
    })();
    return usersPromise;
  },

  invalidatePharmacy: () => set({ isPharmacyLoaded: false, pharmacy: null }),
  invalidateUsers: () => set({ isUsersLoaded: false, users: [] }),
  reset: () =>
    set({
      pharmacy: null,
      users: [],
      isPharmacyLoaded: false,
      isUsersLoaded: false,
    }),
}));
